import type { EventIndex, Explorer } from '../types'
import { type SubscanClient, createSubscanClient } from './client'
import { getEvents } from './getEvents'

/** Subscan events API の 1 request あたりの取得件数 */
const subscanEventsPageSize = 25

type CreateSubscanParameters = {
  name: string
  baseUrl: string
  networkName: string
  apiKey?: string
  proxyUrl?: string
}

type GetAddressEventsParameters = {
  client: SubscanClient
  address: string
  fromBlock?: number
  toBlock?: number
  createHeaders?: () => Promise<Record<string, string>>
  onFetched?: (parameters: {
    events: readonly EventIndex[]
    toBlock: number
    completed: boolean
  }) => Promise<void>
}

/**
 * Subscan events API から address の event を古い block から順に取得する。
 * @param params - Subscan client、検索範囲、ページ取得時の callback
 * @returns 重複を除いた event 一覧
 */
export const getAddressEvents = async ({
  client,
  address,
  fromBlock = 1,
  toBlock,
  createHeaders,
  onFetched,
}: GetAddressEventsParameters): Promise<readonly EventIndex[]> => {
  const events = new Map<string, EventIndex>()
  const endBlock = toBlock
  let startBlock = fromBlock
  let page = 0

  while (true) {
    const result = await getEvents({
      client,
      address,
      page,
      fromBlock: startBlock,
      toBlock: endBlock,
      headers: createHeaders === undefined ? undefined : await createHeaders(),
    })
    for (const event of result.events) {
      if (!events.has(event.event_index)) {
        events.set(event.event_index, event)
      }
    }

    const completed = result.events.length < subscanEventsPageSize
    if (completed) {
      if (onFetched !== undefined && endBlock !== undefined) {
        await onFetched({
          events: result.events,
          toBlock: endBlock,
          completed: true,
        })
      }
      break
    }

    if (endBlock === undefined) {
      page += 1
      continue
    }

    const firstEventBlockNumber = Number(
      result.events[0].event_index.split('-')[0],
    )
    let minBlockNumber = firstEventBlockNumber
    let maxBlockNumber = firstEventBlockNumber
    for (const event of result.events) {
      const blockNumber = Number(event.event_index.split('-')[0])
      if (blockNumber < minBlockNumber) {
        minBlockNumber = blockNumber
      }
      if (blockNumber > maxBlockNumber) {
        maxBlockNumber = blockNumber
      }
    }

    if (onFetched !== undefined) {
      await onFetched({
        events: result.events,
        toBlock: maxBlockNumber - 1,
        completed: false,
      })
    }

    if (minBlockNumber === maxBlockNumber) {
      page += 1
    } else {
      page = 0
      startBlock = maxBlockNumber
    }
  }

  return [...events.values()]
}

export const createSubscan = ({
  name,
  baseUrl,
  networkName,
  apiKey,
  proxyUrl,
}: CreateSubscanParameters): Explorer => {
  const client = createSubscanClient({
    networkName,
    apiKey,
    proxyUrl,
  })

  return {
    name,
    baseUrl,
    getAddressEvents: async (parameters) =>
      await getAddressEvents({ client, ...parameters }),
  }
}
